"use client";
import { ReactNode, useEffect, useCallback, useRef } from "react";

interface Props {
  children: ReactNode;
  loading: React.ReactNode;
  next: (page: number) => void;
  hasMore: boolean;
  isLoading: boolean;
  skeletonElementId: string;
  dataLength: number;
}

export default function InfiniteScroll({
  children,
  isLoading,
  skeletonElementId,
  loading,
  hasMore,
  next,
  dataLength,
}: Props) {
  const pageRef = useRef(0);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const handleIntersection = useCallback(
    (entries: IntersectionObserverEntry[]) => {
      const [entry] = entries;
      if (entry.isIntersecting && hasMore && !isLoading) {
        const nextPage = pageRef.current + 1;
        pageRef.current = nextPage;
        next(nextPage);
      }
    },
    [hasMore, isLoading, next],
  );

  useEffect(() => {
    if (!hasMore) return;

    const skeletonElement = document.getElementById(skeletonElementId);

    if (skeletonElement) {
      observerRef.current = new IntersectionObserver(handleIntersection, {
        root: null,
        rootMargin: "0px",
        threshold: 0.1,
      });
      observerRef.current.observe(skeletonElement);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [skeletonElementId, handleIntersection, hasMore, dataLength]);

  return (
    <>
      {children}
      {hasMore && loading}
    </>
  );
}
